import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { FileText, GripVertical, Download, Edit2, Trash2, Layers, Loader2, CheckSquare, Square, ArrowRight, Inbox } from 'lucide-react';
import { openAuthed } from '../lib/authSession';

const BUCKETS = [
  { key: 'application', label: 'Application', color: 'border-sky-300 bg-sky-50/60', head: 'bg-sky-600' },
  { key: 'disclosure', label: 'Disclosure', color: 'border-violet-300 bg-violet-50/60', head: 'bg-violet-600' },
  { key: 'other', label: 'Other Documents', color: 'border-amber-300 bg-amber-50/60', head: 'bg-amber-600' },
];

const fmtSize = (b) => (!b ? '0 B' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const sortBucket = (a, b) => (a.bucket_order || 0) - (b.bucket_order || 0) || String(a.created_at).localeCompare(String(b.created_at));

/**
 * ClientDocsBoard — manual compliance-package workflow:
 * uploaded docs (each with a tick box) are dragged into Application / Disclosure /
 * Other Documents, then "Merge" creates the 3 packages (ticked files only).
 */
export const ClientDocsBoard = ({ apiUrl, folderId, files, onRefresh, onRename, onDelete, onMerged }) => {
  const [unticked, setUnticked] = useState(() => new Set(files.filter(f => f.merge_excluded).map(f => f.id)));
  const [selected, setSelected] = useState(() => new Set()); // for "Move selected to…"
  const [dragId, setDragId] = useState(null);
  const [overBucket, setOverBucket] = useState(null);
  const [merging, setMerging] = useState(false);

  const sources = useMemo(() => files.filter(f => !f.is_merged_package), [files]);
  const pool = useMemo(() => sources.filter(f => !f.bucket_override).sort(sortBucket), [sources]);
  const inBucket = (key) => sources.filter(f => f.bucket_override === key).sort(sortBucket);
  const assignedCount = sources.length - pool.length;

  const persist = async (assignments) => {
    await fetch(`${apiUrl}/api/client-files/${folderId}/bucket-assign`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assignments }),
    });
    onRefresh?.();
  };

  const moveTo = async (ids, bucket, beforeId = null) => {
    const list = bucket ? inBucket(bucket).filter(f => !ids.includes(f.id)) : [];
    const idx = beforeId ? list.findIndex(f => f.id === beforeId) : -1;
    const moving = sources.filter(f => ids.includes(f.id));
    const next = idx >= 0 ? [...list.slice(0, idx), ...moving, ...list.slice(idx)] : [...list, ...moving];
    const assignments = bucket
      ? next.map((f, i) => ({ file_id: f.id, bucket, order: i + 1 }))
      : moving.map(f => ({ file_id: f.id, bucket: null, order: 0 }));
    setSelected(new Set());
    await persist(assignments);
  };

  const onDropBucket = (e, bucket, beforeId = null) => {
    e.preventDefault(); e.stopPropagation();
    setOverBucket(null);
    const id = e.dataTransfer.getData('text/utmost-file') || dragId;
    if (!id) return;
    const ids = selected.has(id) ? [...selected] : [id];
    moveTo(ids, bucket, beforeId);
    setDragId(null);
  };

  const toggleTick = (id) => {
    const willExclude = !unticked.has(id);
    setUnticked(prev => { const n = new Set(prev); willExclude ? n.add(id) : n.delete(id); return n; });
    const f = sources.find(x => x.id === id);
    fetch(`${apiUrl}/api/client-files/${folderId}/bucket-assign`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignments: [{ file_id: id, bucket: f?.bucket_override || null, order: f?.bucket_order || 0, excluded: willExclude }] }),
    });
  };
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleMerge = async () => {
    if (assignedCount === 0) return;
    setMerging(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folderId}/merge-buckets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ exclude_ids: [...unticked] }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { alert(d.detail || 'Merge failed.'); return; }
      const made = (d.results || []).filter(x => x.package).map(x => BUCKETS.find(b => b.key === x.package_type)?.label);
      alert(made.length ? `Created ${made.length} package(s): ${made.join(', ')}.\n\nNext: complete the Compliance Checklist — the final package is then built automatically.` : 'No packages created (all buckets empty or unticked).');
      onMerged?.();
    } finally {
      setMerging(false);
    }
  };

  // Plain render fn (NOT a nested component): a nested component type changes every render,
  // so React remounted the dragged row on dragstart and Chrome aborted the HTML5 drag.
  const renderFileRow = ({ file, bucket }) => {
    const ticked = !unticked.has(file.id);
    const isSel = selected.has(file.id);
    return (
      <div
        key={file.id}
        draggable
        onDragStart={(e) => { setDragId(file.id); e.dataTransfer.setData('text/utmost-file', file.id); e.dataTransfer.setData('text/plain', file.id); e.dataTransfer.effectAllowed = 'move'; }}
        onDragEnd={() => { setDragId(null); setOverBucket(null); }}
        onDragOver={(e) => { if (bucket) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; } }}
        onDrop={(e) => { if (bucket) onDropBucket(e, bucket, file.id); }}
        style={{ userSelect: 'none', WebkitUserDrag: 'element' }}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-md border bg-white cursor-grab active:cursor-grabbing transition-shadow hover:shadow ${dragId === file.id ? 'opacity-40' : ''} ${isSel ? 'ring-2 ring-[#0077B6]' : 'border-gray-200'} ${!ticked ? 'opacity-60' : ''}`}
        data-testid={`doc-row-${file.id}`}
      >
        <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0 pointer-events-none" />
        <button type="button" onClick={() => toggleTick(file.id)} title={ticked ? 'Included in merge' : 'Excluded from merge'} className="flex-shrink-0" data-testid={`tick-file-${file.id}`}>
          {ticked ? <CheckSquare className="h-5 w-5 text-emerald-600" /> : <Square className="h-5 w-5 text-gray-400" />}
        </button>
        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <div className="min-w-0 flex-1" onClick={() => toggleSelect(file.id)} title="Click to select for 'Move selected'">
          <p className="text-sm text-gray-800 break-words leading-tight" title={file.filename}>{file.filename}</p>
          <p className="text-[11px] text-gray-500">{fmtSize(file.file_size)} • {new Date(file.created_at).toLocaleDateString()}{file.uploaded_via === 'client_request' && <span className="text-emerald-700"> • client-submitted</span>}</p>
        </div>
        <div className="flex items-center flex-shrink-0">
          <button onClick={() => onRename(file.id, file.filename)} className="p-1 text-gray-500 hover:text-gray-900" title="Rename" data-testid={`rename-file-${file.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
          <a href={`${apiUrl}/api/client-files/download/${file.id}`} onClick={openAuthed} draggable={false} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-500 hover:text-gray-900" title="Download" data-testid={`download-file-${file.id}`}><Download className="h-3.5 w-3.5" /></a>
          <button onClick={() => onDelete(file.id)} className="p-1 text-red-500 hover:text-red-700" title="Delete" data-testid={`delete-file-${file.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="client-docs-board">
      {/* Step 1: uploaded pool */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setOverBucket('pool'); }}
        onDragLeave={() => setOverBucket(null)}
        onDrop={(e) => onDropBucket(e, null)}
        className={`rounded-lg border-2 border-dashed p-3 transition-colors ${overBucket === 'pool' ? 'border-[#0077B6] bg-blue-50' : 'border-gray-200'}`}
        data-testid="docs-pool"
      >
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Inbox className="h-4 w-4 text-gray-500" />Uploaded Documents <span className="text-xs font-normal text-gray-500">({pool.length} unassigned of {sources.length})</span>
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <span className="text-gray-600">Move {selected.size} selected to</span>
              {BUCKETS.map(b => (
                <button key={b.key} onClick={() => moveTo([...selected], b.key)} className={`px-2 py-1 rounded text-white ${b.head}`} data-testid={`move-selected-${b.key}`}>{b.label}</button>
              ))}
              <button onClick={() => moveTo([...selected], null)} className="px-2 py-1 rounded border text-gray-600" data-testid="move-selected-pool">Unassign</button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-2">Step 1 — Drag each document into Application, Disclosure or Other Documents below (or click rows to select, then "Move selected to"). Untick a document to leave it out of the merge.</p>
        {pool.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3">{sources.length === 0 ? 'No documents uploaded yet.' : 'All documents have been assigned.'}</p>
        ) : (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1" data-testid="docs-pool-list">{pool.map(f => renderFileRow({ file: f, bucket: null }))}</div>
        )}
      </div>

      {/* Step 2: buckets */}
      <div className="grid md:grid-cols-3 gap-3">
        {BUCKETS.map(b => {
          const items = inBucket(b.key);
          return (
            <div
              key={b.key}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setOverBucket(b.key); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOverBucket(null); }}
              onDrop={(e) => onDropBucket(e, b.key)}
              className={`rounded-lg border-2 ${b.color} min-h-[160px] flex flex-col transition-all ${overBucket === b.key ? 'ring-2 ring-offset-1 ring-[#0077B6]' : ''}`}
              data-testid={`bucket-${b.key}`}
            >
              <div className={`${b.head} text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-t-md flex items-center justify-between`}>
                {b.label}<span className="font-normal normal-case" data-testid={`bucket-count-${b.key}`}>{items.filter(f => !unticked.has(f.id)).length}/{items.length} files</span>
              </div>
              <div className="p-2 space-y-1.5 flex-1">
                {items.length === 0 ? <p className="text-xs text-gray-400 text-center py-6">Drop documents here</p> : items.map(f => renderFileRow({ file: f, bucket: b.key }))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-gray-500">Step 2 — Merge creates three PDFs named <b>Application</b>, <b>Disclosure</b> and <b>Other Documents</b> (replacing previous versions). Step 3 — complete the Compliance Checklist; the final package is then assembled automatically.</p>
        <Button onClick={handleMerge} disabled={merging || assignedCount === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="merge-buckets-btn">
          {merging ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Layers className="h-4 w-4 mr-1" />}
          Merge into 3 Packages <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default ClientDocsBoard;
